// LotLingo Portal - Formatting Utilities
// Pure functions for currency, dates, percentages, and display formatting

// Re-export shared currency utilities
export {
  roundToTwo,
  formatUsd,
  parseCurrency as parseCurrencyValue,
  sanitizePercentage,
  sanitizeCurrency,
  formatPercentageInput,
  formatCurrencyInput,
} from '@lotlingo/shared';

// Re-export shared HTML utilities
export {
  escapeHtml,
  escapeAttr,
  unescapeHtml,
  capitalize,
  truncate,
} from '../../shared/htmlUtils.js';

// Alias formatUsd as formatCurrencyDisplay for backwards compatibility
export { formatUsd as formatCurrencyDisplay } from '@lotlingo/shared';

// Import for internal use
import { escapeHtml } from '../../shared/htmlUtils.js';
import {
  roundToTwo,
  formatUsd as formatCurrencyDisplay,
  parseCurrency as parseCurrencyValue,
} from '@lotlingo/shared';

export function formatMeatOnBoneValue(amount: number): string {
  if (typeof amount !== 'number' || !Number.isFinite(amount)) {
    return '—';
  }
  const formatted = formatCurrencyDisplay(amount);
  if (!formatted) {
    return '—';
  }
  return '<span class="text-strong">' + escapeHtml(formatted) + '</span>';
}

export function getMeatBadgeColor(meatLow?: number | null, meatHigh?: number | null): string {
  const lowIsNegative = meatLow != null && meatLow < 0;
  const highIsNegative = meatHigh != null && meatHigh < 0;

  if (lowIsNegative && highIsNegative) {
    return 'red';
  } else if (lowIsNegative) {
    return 'yellow';
  }
  return 'green';
}

interface EstimateSource {
  isShared?: boolean;
}

export function renderEstimateSourceBadge(estimateSource?: EstimateSource | null): string {
  if (!estimateSource || typeof estimateSource !== 'object') {
    return '';
  }
  const isShared = estimateSource.isShared === true;
  const badgeClass = isShared ? 'estimate-source-badge--shared' : 'estimate-source-badge--new';
  const badgeText = isShared ? 'Shared' : 'New';
  const title = isShared
    ? "This estimate was reused from another user's request to save AI credits"
    : 'This estimate was freshly computed for this lot';
  return (
    '<span class="estimate-source-badge ' +
    badgeClass +
    '" title="' +
    escapeHtml(title) +
    '">' +
    badgeText +
    '</span>'
  );
}

export function formatEstimateInputValue(amount: number | null | undefined, raw: unknown): string {
  if (typeof amount === 'number' && Number.isFinite(amount)) {
    return String(roundToTwo(amount));
  }
  const parsed = parseCurrencyValue(raw);
  if (parsed != null) {
    return String(roundToTwo(parsed));
  }
  return '';
}

export function sanitizeEstimateInputString(value?: string | null): string {
  let input = (value || '').toString();
  input = input.replace(/[^0-9.]/g, '');
  if (!input) {
    return '';
  }
  const firstDot = input.indexOf('.');
  const before = input.slice(0, firstDot + 1);
  const after = input
    .slice(firstDot + 1)
    .split('.')
    .join('');
  input = before + after;
  const dotIndex = input.indexOf('.');
  if (dotIndex === 0) {
    input = '0' + input;
  }
  if (dotIndex > -1) {
    const integerPart = input.slice(0, dotIndex);
    const decimalPart = input.slice(dotIndex + 1, dotIndex + 3);
    input = decimalPart.length ? integerPart + '.' + decimalPart : integerPart + '.';
  }
  return input;
}

export function parseEstimateInputValue(value?: string | null): number | null {
  if (typeof value !== 'string') {
    return null;
  }
  let trimmed = value.trim();
  if (!trimmed) {
    return null;
  }
  if (trimmed === '.') {
    return null;
  }
  if (trimmed.endsWith('.')) {
    trimmed = trimmed.slice(0, -1);
  }
  if (!trimmed) {
    return null;
  }
  const validPattern = /^[0-9]+(?:[.][0-9]{1,2})?$/;
  if (!validPattern.test(trimmed)) {
    return null;
  }
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed)) {
    return null;
  }
  return roundToTwo(parsed);
}

export function formatAiRawAmount(amount: number): string {
  return formatCurrencyDisplay(amount) ?? '';
}

interface DateSettings {
  timeZone?: string;
}

import { createLogger } from '../../shared/logger.js';
import { extractErrorMessage } from '../../shared/errorUtils.js';

const log = createLogger('portal:formatting');

export function fmtDate(
  ts: number | string | Date | null | undefined,
  settings?: DateSettings | null
): string {
  if (!ts) return '';
  const d = new Date(ts);
  try {
    const tz = settings && settings.timeZone;
    return tz ? d.toLocaleString(undefined, { timeZone: tz }) : d.toLocaleString();
  } catch (_e) {
    log.debug('Failed to format date with timezone; falling back', {
      error: extractErrorMessage(_e),
      ts: typeof ts === 'object' ? ts.toString() : ts,
      settings,
    });
    return d.toLocaleString();
  }
}

export function formatCountdown(closeTs?: number | null): { text: string; isUrgent: boolean } {
  if (!closeTs || !Number.isFinite(closeTs)) {
    return { text: '-', isUrgent: false };
  }
  const now = Date.now();
  const diff = closeTs - now;
  if (diff <= 0) return { text: 'CLOSED', isUrgent: false };

  const totalMinutes = Math.floor(diff / (1000 * 60));
  const totalHours = Math.floor(totalMinutes / 60);
  const totalDays = Math.floor(totalHours / 24);

  const days = totalDays;
  const hours = totalHours % 24;
  const minutes = totalMinutes % 60;

  const isUrgent = diff < 5 * 60 * 1000;

  // Use abbreviated format: "17h 29m" instead of "17 hours, 29 minutes"
  const parts: string[] = [];
  if (days > 0) parts.push(days + 'd');
  if (hours > 0 || days > 0) parts.push(hours + 'h');
  parts.push(minutes + 'm');

  return { text: parts.join(' '), isUrgent };
}

export function cap(s?: string | null): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : '';
}

// NOTE: Duplicate re-export of escapeHtml / escapeAttr removed to avoid build error

export function toNumber(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

export function getSourceDisplay(source?: string | null, cssClass?: string): string {
  const cls = cssClass || 'source-logo';

  // Normalize extension sources to their base names for logo display
  const normalizedSource =
    source === 'extension-hibid' ? 'hibid' : source === 'extension-kbid' ? 'kbid' : source;

  if (normalizedSource === 'kbid') {
    return (
      '<img src="https://286af9ae68680167a2ec-73c810ed5df364485e5a629884512f7c.ssl.cf2.rackcdn.com/site_logo/9aed527eb4f852dfe443cd190a0c8830.png" alt="K-BID" class="' +
      cls +
      '" title="K-BID" />'
    );
  } else if (normalizedSource === 'hibid') {
    return (
      '<img src="https://cdn.hibid.com/cdn/images/logos/hibid//mainlogo.png" alt="HiBid" class="' +
      cls +
      '" title="HiBid" />'
    );
  }

  // For other sources, return text
  const sourceMap: Record<string, string> = {
    manual: 'Manual',
    import: 'Import',
  };
  return sourceMap[normalizedSource as string]
    ? escapeHtml(sourceMap[normalizedSource as string])
    : escapeHtml(normalizedSource || '-');
}

export function getUpdatedTimestamp(lot?: {
  updatedAt?: number | string;
  createdAt?: number | string;
}): number | undefined {
  if (!lot) return undefined;
  const { updatedAt, createdAt } = lot;
  if (typeof updatedAt === 'number' && Number.isFinite(updatedAt)) return updatedAt;
  if (typeof updatedAt === 'string') {
    const parsed = Date.parse(updatedAt);
    if (Number.isFinite(parsed)) return parsed;
  }
  if (typeof createdAt === 'number' && Number.isFinite(createdAt)) return createdAt;
  if (typeof createdAt === 'string') {
    const parsed = Date.parse(createdAt);
    if (Number.isFinite(parsed)) return parsed;
  }
  return undefined;
}
